import * as React from 'react';

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  fillOpacity?: number;
  strokeWidth?: number;
}

/**
 * Tiny inline-SVG sparkline. No deps. Used in dashboard KPI cards.
 * Pass a small array of numeric values; component normalises and renders.
 */
const Sparkline: React.FC<SparklineProps> = ({
  data,
  width = 120,
  height = 28,
  color = '#1976d2',
  fillOpacity = 0.08,
  strokeWidth = 1.6,
}) => {
  if (!data || data.length === 0) {
    return <svg width={width} height={height} role="img" aria-label="sparkline" />;
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = data.length > 1 ? width / (data.length - 1) : width;

  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y] as const;
  });

  const linePath = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${width} ${height} L0 ${height} Z`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" role="img" aria-label="sparkline">
      <path d={areaPath} fill={color} opacity={fillOpacity} />
      <path d={linePath} stroke={color} strokeWidth={strokeWidth} fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

export default Sparkline;
